import React, { useMemo } from 'react';
import { Table, Grid } from 'antd';
import { formatDateTime } from '../../utils/dateFormat';

const DataTable = ({ data, aggregationLevel, loading }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Interval',
        dataIndex: 'interval_datetime',
        key: 'interval_datetime',
        render: (text) => formatDateTime(text),
        sorter: (a, b) => new Date(a.interval_datetime) - new Date(b.interval_datetime),
        width: 180
      },
      {
        title: 'Region',
        dataIndex: 'region_id',
        key: 'region_id',
        filters: [
          { text: 'NSW1', value: 'NSW1' },
          { text: 'QLD1', value: 'QLD1' },
          { text: 'VIC1', value: 'VIC1' },
          { text: 'SA1', value: 'SA1' },
          { text: 'TAS1', value: 'TAS1' }
        ],
        onFilter: (value, record) => record.region_id === value,
        width: 100
      },
      {
        title: 'MaxAvail (MW)',
        dataIndex: 'maxavail',
        key: 'maxavail',
        render: (value) => value !== null && value !== undefined ? value.toFixed(2) : 'N/A',
        sorter: (a, b) => (a.maxavail || 0) - (b.maxavail || 0),
        width: 140
      }
    ];

    // Add columns based on aggregation level
    if (aggregationLevel === 'duid') {
      return [
        ...baseColumns.slice(0, 1),
        {
          title: 'DUID',
          dataIndex: 'duid',
          key: 'duid',
          width: 120
        },
        {
          title: 'Station',
          dataIndex: 'station_name',
          key: 'station_name',
          width: 200
        },
        ...baseColumns.slice(1),
        {
          title: 'Fuel Type',
          dataIndex: 'fuel_type',
          key: 'fuel_type',
          filters: Array.from(new Set(data.map(d => d.fuel_type).filter(Boolean))).map(ft => ({
            text: ft,
            value: ft
          })),
          onFilter: (value, record) => record.fuel_type === value,
          width: 120
        },
        {
          title: 'Capacity (MW)',
          dataIndex: 'capacity',
          key: 'capacity',
          render: (value) => value !== null && value !== undefined ? value.toFixed(2) : 'N/A',
          width: 140
        }
      ];
    } else if (aggregationLevel === 'station') {
      return [
        ...baseColumns.slice(0, 1),
        {
          title: 'Station ID',
          dataIndex: 'stationid',
          key: 'stationid',
          width: 120
        },
        {
          title: 'Station Name',
          dataIndex: 'station_name',
          key: 'station_name',
          width: 200
        },
        ...baseColumns.slice(1),
        {
          title: 'Fuel Type',
          dataIndex: 'fuel_type',
          key: 'fuel_type',
          width: 120
        },
        {
          title: 'DUID Count',
          dataIndex: 'duid_count',
          key: 'duid_count',
          width: 120
        },
        {
          title: 'Capacity (MW)',
          dataIndex: 'capacity',
          key: 'capacity',
          render: (value) => value !== null && value !== undefined ? value.toFixed(2) : 'N/A',
          width: 140
        }
      ];
    } else if (aggregationLevel === 'portfolio') {
      return [
        ...baseColumns.slice(0, 1),
        {
          title: 'Portfolio',
          dataIndex: 'portfolioid',
          key: 'portfolioid',
          width: 150
        },
        ...baseColumns.slice(1),
        {
          title: 'Station Count',
          dataIndex: 'station_count',
          key: 'station_count',
          width: 120
        },
        {
          title: 'Fuel Type Count',
          dataIndex: 'fuel_type_count',
          key: 'fuel_type_count',
          width: 140
        },
        {
          title: 'Capacity (MW)',
          dataIndex: 'capacity',
          key: 'capacity',
          render: (value) => value !== null && value !== undefined ? value.toFixed(2) : 'N/A',
          width: 140
        }
      ];
    } else if (aggregationLevel === 'fuel_type') {
      return [
        ...baseColumns.slice(0, 1),
        {
          title: 'Fuel Type',
          dataIndex: 'fuel_type',
          key: 'fuel_type',
          width: 150
        },
        ...baseColumns.slice(1),
        {
          title: 'Station Count',
          dataIndex: 'station_count',
          key: 'station_count',
          width: 120
        },
        {
          title: 'Portfolio Count',
          dataIndex: 'portfolio_count',
          key: 'portfolio_count',
          width: 140
        },
        {
          title: 'Capacity (MW)',
          dataIndex: 'capacity',
          key: 'capacity',
          render: (value) => value !== null && value !== undefined ? value.toFixed(2) : 'N/A',
          width: 140
        }
      ];
    }

    return baseColumns;
  }, [aggregationLevel, data]);

  const mobileHiddenKeys = ['station_name', 'capacity'];
  const displayColumns = isMobile
    ? columns.filter(col => !mobileHiddenKeys.includes(col.key))
    : columns;

  return (
    <Table
      columns={displayColumns}
      dataSource={data}
      loading={loading}
      rowKey={(record) => `${record.interval_datetime}-${record.duid || record.stationid || record.portfolioid || record.fuel_type}`}
      pagination={isMobile ? {
        pageSize: 20,
        simple: true,
      } : {
        pageSize: 50,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} rows`
      }}
      scroll={isMobile ? { x: 600, y: 400 } : { x: 1200, y: 600 }}
      size="small"
    />
  );
};

export default DataTable;
